import { useContext, useState } from 'react';
import * as Sentry from "@sentry/react";
import { useCreatePost, useSchedulePost } from '@/lib/api/requests/posts.requests';
import { annotateActiveSpan, withTelemetrySpan } from '@/lib/monitoring/telemetry';
import { PostGenerationResult } from '../services/PostGenerationService';
import { toast } from "@/lib/toast";
import { Context } from '@/util-components/root/context';

export interface PostPublishingOptions {
  draftSessionId?: string;
  accountId: string | null;
  postToInstagram: boolean;
  postToFacebook: boolean;
  postToWhatsapp?: boolean;
  whatsappGroupId?: string | null;
  format: 'post' | 'story' | null;
  caption: string;
  generatedImageUrl?: string | null;
  generationResult: PostGenerationResult | null;
  scheduledFor?: Date | null;
  onSuccess?: () => void;
}

/**
 * Centralized error handler for posting failures
 */
const handlePostingError = (error: any) => {
  console.error("Posting failed", error);
  
  let userMessage = "Ein unbekannter Fehler ist aufgetreten.";
  let description = "Bitte versuchen Sie es später erneut.";

  // Extract Backend Error Data
  const responseData = error.response?.data ?? error.debugContext?.response?.body;
  const backendError = responseData?.error ?? error.error; // e.g., 'MetaRequestFailed', 'MetaOauthException'
  
  // Extract message (handling string or array from NestJS)
  const rawMessage = responseData?.message ?? error.backendMessage;
  const backendMessage = Array.isArray(rawMessage) ? rawMessage.join(", ") : rawMessage;
  
  const metaData = responseData?.metaData ?? error.metaData;
  const status = error.response?.status ?? error.statusCode;

  // --- 1. META API ERRORS (Wrapped in 500 or specific exceptions) ---
  if (backendError === 'MetaOauthException' || (metaData?.error?.code === 190)) {
    userMessage = "Verbindung abgelaufen";
    description = "Ihre Verbindung zu Facebook/Instagram ist nicht mehr gültig. Bitte verbinden Sie das Konto in den Einstellungen neu.";
  }
  else if (backendError === 'MetaRequestFailed') {
    userMessage = "Fehler bei Facebook/Instagram";
    const metaError = metaData?.error || {};
    const code = metaError.code;
    const msg = metaError.message || backendMessage;
    const userTitle = metaError.error_user_title;
    const userMsg = metaError.error_user_msg;

    // Use Meta's user-facing message if available
    if (userTitle && userMsg) {
      userMessage = userTitle;
      description = userMsg;
    } 
    // Map common Meta error codes to user-friendly messages
    else if (code === 368) {
       description = "Ihr Account wurde vorübergehend für diese Aktion blockiert (Spam-Schutz).";
    } else if (code === 4 || code === 17 || code === 32) {
       description = "Sie haben zu viele Anfragen gesendet. Bitte warten Sie einige Minuten.";
    } else if (code === 100) {
       description = "Die Anfrage war ungültig. Möglicherweise entspricht das Bildformat nicht den Anforderungen.";
    } else if (code === 10) {
       description = "Fehlende Berechtigung. Bitte prüfen Sie die Rechte der App in Ihren Facebook-Einstellungen.";
    } else {
       // Clean up technical message
       const cleanMsg = msg ? `(${msg})` : "";
       description = `Der Social-Media-Anbieter hat die Anfrage abgelehnt. ${cleanMsg}`;
    }
  }

  // --- 2. FILE SIZE & VALIDATION ERRORS (400, 413, 422) ---
  else if (backendError === 'TotalFileSizeExceededBadRequest' || status === 413) {
    userMessage = "Datei zu groß";
    description = "Das generierte Bild ist zu groß für den Upload (Max. 10MB). Bitte versuchen Sie es erneut.";
  }
  else if (status === 422) {
     userMessage = "Verarbeitung fehlgeschlagen";
     // Check for specific backend messages
     if (typeof backendMessage === 'string') {
        if (backendMessage.includes('Container')) {
           description = "Instagram konnte das Bild nicht verarbeiten. Bitte versuchen Sie es später erneut.";
        } else if (backendMessage.includes('size') || backendMessage.includes('Validation')) {
           description = "Die Datei ist ungültig oder zu groß.";
        } else {
           description = backendMessage;
        }
     } else {
        description = "Die Anfrage konnte nicht verarbeitet werden.";
     }
  }
  else if (status === 400) {
     userMessage = "Ungültige Anfrage";
     if (typeof backendMessage === 'string' && backendMessage.includes('Validation failed')) {
        description = "Das Dateiformat wird nicht unterstützt oder die Datei ist zu groß.";
     } else if (backendMessage) {
        description = backendMessage;
     } else {
        description = "Die Anfrage war fehlerhaft (400).";
     }
  }

  // --- 3. AUTH & NETWORK ERRORS ---
  else if (status === 401) {
    userMessage = "Nicht autorisiert";
    description = "Ihre Sitzung ist abgelaufen. Bitte laden Sie die Seite neu.";
  }
  else if (status === 408 || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    userMessage = "Zeitüberschreitung";
    description = "Das Posten hat zu lange gedauert. Das Bild war möglicherweise zu groß oder die Internetverbindung zu langsam.";
  }
  else if (error.message === 'Network Error') {
    userMessage = "Verbindungsfehler";
    description = "Der Server konnte nicht erreicht werden. Bitte prüfen Sie Ihre Internetverbindung.";
  }
  else if (error.message === "Could not get canvas context" || error.message === "Could not create blob") {
    userMessage = "Bildverarbeitung fehlgeschlagen";
    description = "Das Bild konnte nicht für den Upload vorbereitet werden. Bitte versuchen Sie es erneut oder wählen Sie ein anderes Bild.";
  }
  else {
    // Fallback to backend message if available
    if (backendMessage) {
      userMessage = "Fehler beim Posten";
      description = backendMessage;
    } else if (error.message) {
       description = error.message;
    }
  }

  toast.error(userMessage, {
    description: description,
    duration: 6000,
    error,
    action: {
      label: "Schließen",
      onClick: () => {}
    }
  });
};

function getSelectedPlatforms(
  postToInstagram: boolean,
  postToFacebook: boolean,
  postToWhatsapp: boolean | undefined,
  format: 'post' | 'story' | null,
): string[] {
  return [
    postToInstagram ? (format === 'story' ? 'instagram_story' : 'instagram_post') : null,
    postToFacebook ? (format === 'story' ? 'facebook_story' : 'facebook_post') : null,
    postToWhatsapp ? 'whatsapp' : null,
  ].filter((value): value is string => Boolean(value));
}

function getPlatformTag(selectedPlatforms: string[]): string {
  const normalizedPlatforms = Array.from(
    new Set(
      selectedPlatforms.map((platform) => {
        if (platform.startsWith('instagram')) return 'instagram';
        if (platform.startsWith('facebook')) return 'facebook';
        return 'whatsapp';
      }),
    ),
  );

  return normalizedPlatforms.length === 1 ? normalizedPlatforms[0] : 'multi';
}

function getIntegrationTag(selectedPlatforms: string[]): string {
  const hasMeta = selectedPlatforms.some((platform) => platform.startsWith('instagram') || platform.startsWith('facebook'));
  const hasWhatsapp = selectedPlatforms.includes('whatsapp');

  if (hasMeta && hasWhatsapp) return 'multi';
  if (hasMeta) return 'meta';
  return 'whatsapp';
}

function capturePublishFailure(
  error: any,
  options: {
    accountId: string | null;
    scheduledFor?: Date | null;
    selectedPlatforms: string[];
    whatsappGroupId?: string | null;
  },
): void {
  const statusCode = error?.response?.status;
  const normalizedStatusCode = statusCode ?? error?.statusCode;

  // 5xx API failures are already captured centrally in the API client.
  if (typeof normalizedStatusCode === 'number' && normalizedStatusCode >= 500) {
    return;
  }

  const responseData = error?.response?.data ?? error?.debugContext?.response?.body;
  const traceId = responseData?.traceId ?? error?.traceId;
  const correlationId = responseData?.correlationId ?? error?.correlationId;
  const backendError =
    typeof responseData?.error === 'string' ? responseData.error : typeof error?.error === 'string' ? error.error : undefined;
  const rawMessage = responseData?.message ?? error?.backendMessage ?? error?.message;
  const backendMessage = Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage;
  const metaData = responseData?.metaData ?? error?.metaData;
  const metaStatusCode = responseData?.metaStatusCode ?? error?.metaStatusCode;
  const platformTag = getPlatformTag(options.selectedPlatforms);
  const integrationTag = getIntegrationTag(options.selectedPlatforms);

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('feature', 'post_publish');
    scope.setTag('publish_mode', options.scheduledFor ? 'scheduled' : 'direct');
    scope.setTag('platform', platformTag);
    scope.setTag('integration', integrationTag);
    scope.setTag('publish_rejected', true);

    if (typeof normalizedStatusCode === 'number') {
      scope.setTag('http_status', normalizedStatusCode);
    }

    if (backendError) {
      scope.setTag('response_error', backendError);
    }

    if (traceId) {
      scope.setTag('trace_id', traceId);
    }

    if (correlationId) {
      scope.setTag('correlation_id', correlationId);
    }

    scope.setContext('post_publish', {
      accountId: options.accountId,
      selectedPlatforms: options.selectedPlatforms,
      scheduledFor: options.scheduledFor?.toISOString() ?? null,
      whatsappGroupId: options.whatsappGroupId ?? null,
      statusCode: normalizedStatusCode ?? null,
      backendError: backendError ?? null,
      backendMessage: typeof backendMessage === 'string' ? backendMessage : null,
      traceId: traceId ?? null,
      correlationId: correlationId ?? null,
      metaStatusCode: typeof metaStatusCode === 'number' ? metaStatusCode : null,
    });

    if (metaData) {
      scope.setContext('meta_error', metaData);
    }

    const sentryError =
      error instanceof Error ? error : Object.assign(new Error(typeof backendMessage === 'string' ? backendMessage : 'Post publish failed'), error);

    Sentry.captureException(sentryError);
  });
}

export function usePostPublishing() {
  const { mutateAsync: createPost, isPending: isPostingImmediate } = useCreatePost();
  const { mutateAsync: schedulePost, isPending: isScheduling } = useSchedulePost();
  const { user } = useContext(Context);
  
  const isPosting = isPostingImmediate || isScheduling;
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  /**
   * Optimizes the image ensuring it meets size and dimension requirements.
   * Max size: 6MB
   * Max shortest side: 1080px
   */
  const optimizeImage = async (
    url: string,
  ): Promise<{
    file: File;
    width: number;
    height: number;
    sourceWidth: number;
    sourceHeight: number;
  }> => {
    // Check if we can skip optimization for pre-optimized blob URLs (especially on mobile to avoid crashes)
    if (url.startsWith('blob:')) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        
        // If it's already a JPEG and small enough
        if (blob.type === 'image/jpeg' && blob.size <= 6 * 1024 * 1024) {
           const img = new Image();
           img.src = url;
           await new Promise((resolve, reject) => {
             img.onload = resolve;
             img.onerror = reject;
           });
           
           const shortestSide = Math.min(img.width, img.height);
           // If dimensions are already optimized (<= 1080px shortest side), use it directly
           if (shortestSide <= 1080) {
             console.log('Using pre-optimized image directly', { size: blob.size, width: img.width, height: img.height });
             return {
               file: new File([blob], "post-image.jpg", { type: "image/jpeg" }),
               width: img.width,
               height: img.height,
               sourceWidth: img.width,
               sourceHeight: img.height,
             };
           }
        }
      } catch (e) {
        console.warn('Failed to check blob optimization, falling back to canvas re-compression', e);
      }
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = url;
      img.onload = async () => {
        const sourceWidth = img.width;
        const sourceHeight = img.height;
        let width = img.width;
        let height = img.height;
        
        // Initial resize logic: Ensure shortest side is max 1080px (best practice for IG/FB)
        const shortestSide = Math.min(width, height);
        if (shortestSide > 1080) {
          const ratio = 1080 / shortestSide;
          width *= ratio;
          height *= ratio;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error("Could not get canvas context"));
          return;
        }
        // Fill white background for JPEG (in case of transparency)
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Compression loop
        let quality = 1.0;
        const minQuality = 0.5; // Don't go below 50%
        const maxAttempts = 10;
        let attempts = 0;
        
        const tryCompress = () => {
          canvas.toBlob(
            (b) => {
              if (!b) {
                reject(new Error("Could not create blob"));
                return;
              }
              
              if (b.size <= 6 * 1024 * 1024 || attempts >= maxAttempts || quality <= minQuality) {
                // Success or limits reached
                if (b.size > 6 * 1024 * 1024) {
                   console.warn("Could not compress image below 6MB even with reduced quality.");
                }
                // Use JPEG for better compression/quality ratio
                resolve({
                  file: new File([b], "post-image.jpg", { type: "image/jpeg" }),
                  width,
                  height,
                  sourceWidth,
                  sourceHeight,
                });
              } else {
                // Result is still too big, reduce quality and try again
                attempts++;
                quality -= 0.1; // Reduce quality by 10% steps
                tryCompress();
              }
            },
            "image/jpeg",
            quality
          );
        };
        
        tryCompress();
      };
      img.onerror = (e) => reject(e);
    });
  };

  const publishPost = async (options: PostPublishingOptions) => {
    const { 
      draftSessionId,
      accountId, 
      postToInstagram, 
      postToFacebook, 
      postToWhatsapp,
      whatsappGroupId,
      format, 
      caption, 
      generatedImageUrl, 
      generationResult,
      scheduledFor,
      onSuccess 
    } = options;

    if (!generatedImageUrl) return;
    const hasMetaSelection = (postToInstagram || postToFacebook) && !!accountId;
    const hasWhatsappSelection = postToWhatsapp && !!whatsappGroupId;
    if (!hasMetaSelection && !hasWhatsappSelection) return;

    const selectedPlatforms = getSelectedPlatforms(postToInstagram, postToFacebook, postToWhatsapp, format);
    const plan = user?.subscriptionType || 'noplan';
    const tenant = user?.organizationId;
    const platformTag = getPlatformTag(selectedPlatforms);
    const integrationTag = getIntegrationTag(selectedPlatforms);

    try {
      await withTelemetrySpan(
        scheduledFor ? 'post.schedule.submit' : 'post.publish.submit',
        scheduledFor ? 'feature.post.schedule' : 'feature.post.publish',
        {
          'post.platforms.selected': selectedPlatforms,
          'post.platform_count': selectedPlatforms.length,
          'post.template_id': generationResult?.populatedTemplate?.id,
          'post.format': format,
          'post.is_scheduled': Boolean(scheduledFor),
          'post.mentioned_entities.count': generationResult?.entities?.length ?? 0,
        },
        async () => {
          const optimizedImage = await optimizeImage(generatedImageUrl);
          const optimizedFile = optimizedImage.file;

          annotateActiveSpan(
            {
              'post.image.mime_type': optimizedFile.type,
              'post.image.width': optimizedImage.width,
              'post.image.height': optimizedImage.height,
              'post.image.source_width': optimizedImage.sourceWidth,
              'post.image.source_height': optimizedImage.sourceHeight,
              'post.caption.length': caption.length,
              'post.account_selected': Boolean(accountId),
              'post.whatsapp_group_selected': Boolean(whatsappGroupId),
              'post.schedule.delay_seconds': scheduledFor
                ? Math.max(0, Math.round((scheduledFor.getTime() - Date.now()) / 1000))
                : undefined,
            },
            {
              'post.image.size': { value: optimizedFile.size, unit: 'byte' },
              'post.caption.length': { value: caption.length, unit: 'none' },
              'post.mentioned_entities.count': {
                value: generationResult?.entities?.length ?? 0,
                unit: 'none',
              },
            },
          );

          const isStory = format === 'story';
          const formData = new FormData();
          formData.append('file', optimizedFile);
          if (accountId) formData.append('pageId', accountId);
          formData.append('message', caption);
          if (draftSessionId) {
            formData.append('draftSessionId', draftSessionId);
          }
          
          if (generationResult?.populatedTemplate?.id) {
            formData.append('templateId', generationResult.populatedTemplate.id);
          }

          formData.append('instagramPost', String(postToInstagram && !isStory));
          formData.append('facebookPost', String(postToFacebook && !isStory));
          formData.append('instagramStory', String(postToInstagram && isStory));
          formData.append('facebookStory', String(postToFacebook && isStory));

          if (postToWhatsapp && whatsappGroupId) {
            formData.append('whatsappGroupIds', JSON.stringify([whatsappGroupId]));
          }

          if (generationResult?.entities && generationResult.entities.length > 0) {
            const entities = generationResult.entities.map((e) => ({
              type: e.type, 
              id: e.id
            }));
            formData.append('mentionedEntities', JSON.stringify(entities));
          }

          if (scheduledFor) {
            formData.append('scheduledFor', scheduledFor.toISOString());
            await schedulePost(formData);
          } else {
            await createPost(formData);
          }
          
          annotateActiveSpan({
            'post.submit.success': true,
          });

          setIsSuccess(true);
          toast.success(scheduledFor ? "Post erfolgreich eingeplant!" : "Post erfolgreich veröffentlicht!");
          onSuccess?.();
        },
        {
          tags: {
            ...(tenant ? { tenant } : {}),
            plan,
            platform: platformTag,
            integration: integrationTag,
          },
          metricAttributes: {
            plan,
            publish_mode: scheduledFor ? 'scheduled' : 'direct',
            platform: platformTag,
          },
        },
      );
    } catch (error: any) {
      annotateActiveSpan({
        'post.submit.success': false,
        'post.submit.error_name': error?.name,
      });
      capturePublishFailure(error, {
        accountId,
        scheduledFor,
        selectedPlatforms,
        whatsappGroupId,
      });
      handlePostingError(error);
    }
  };

  const reset = () => {
    setIsSuccess(false);
  }

  return {
    publishPost,
    isPosting,
    isSuccess,
    reset
  };
}
